<template>
    <DashboardSectionCard
        :title="t('vue.dashboard_overview_team_title')"
        :subtitle="t('vue.dashboard_overview_team_subtitle')"
    >
        <div class="rounded-md border border-gray-200 bg-gray-50 p-4">
            <div class="flex items-center justify-between gap-3">
                <p class="font-medium text-gray-900">{{ planName }}</p>
                <DashboardStatusBadge
                    :status="planBadgeStatus"
                    :label="planStateLabel"
                />
            </div>
            <p
                v-if="planMeta !== null"
                class="mt-1 text-[13px] leading-[18px] text-gray-500"
            >
                {{ planMeta }}
            </p>
        </div>

        <div v-if="team.members.length > 0" class="my-4 flex items-center">
            <span
                v-for="(member, index) in team.members"
                :key="`${member.initials}-${index}`"
                class="-ml-2 grid h-9 w-9 place-items-center overflow-hidden rounded-full border-2 border-white text-[11px] font-semibold first:ml-0"
                :class="
                    member.owner
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600'
                "
            >
                <img
                    v-if="member.photoUrl !== null"
                    :src="member.photoUrl"
                    :alt="member.initials"
                    class="h-full w-full object-cover"
                />
                <template v-else>{{ member.initials }}</template>
            </span>
            <span
                v-if="hiddenMemberCount > 0"
                class="-ml-2 grid h-9 w-9 place-items-center rounded-full border-2 border-white bg-gray-100 text-[11px] font-semibold text-gray-600"
            >
                +{{ hiddenMemberCount }}
            </span>
        </div>

        <div
            v-for="row in teamRows"
            :key="row.label"
            class="flex min-h-[34px] items-center justify-between border-t border-gray-200 text-[13px] leading-[18px] text-gray-600"
        >
            <span>{{ row.label }}</span>
            <span class="font-medium text-gray-900">{{ row.value }}</span>
        </div>
    </DashboardSectionCard>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { format } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { useTranslations } from "@/composables/useTranslations";
import DashboardSectionCard from "./DashboardSectionCard.vue";
import DashboardStatusBadge from "./DashboardStatusBadge.vue";
import type {
    ISubscriptionOverview,
    ITeamOverview,
    MonitoringStatus,
} from "@/types/Dashboard/IDashboardOverview";

interface TeamRow {
    label: string;
    value: string;
}

const props = defineProps<{
    team: ITeamOverview;
    subscription: ISubscriptionOverview;
    nextBillingAt: string | null;
    billingLoading: boolean;
}>();

const { t, locale } = useTranslations();

const formatDate = (isoDate: string): string =>
    format(new Date(isoDate), "PPP", {
        locale: locale.value === "cs" ? cs : enUS,
    });

const planName = computed<string>(() =>
    props.subscription.state === "god_mode"
        ? t("vue.dashboard_overview_plan_god_mode")
        : t(
              `vue.dashboard_overview_plan_${props.subscription.level.replaceAll("-", "_")}`,
          ),
);

const planStateLabel = computed<string>(() =>
    t(`vue.dashboard_overview_sub_state_${props.subscription.state}`),
);

const planBadgeStatus = computed<MonitoringStatus>(() => {
    switch (props.subscription.state) {
        case "active":
        case "god_mode":
            return "enabled";
        case "past_due":
        case "cancelled":
        case "trial_expired":
            return "attention";
        case "trialing":
            return "partial";
        default:
            return "disabled";
    }
});

const planMeta = computed<string | null>(() => {
    const periodEndsAt = props.subscription.periodEndsAt;

    if (periodEndsAt !== null && props.subscription.state === "trialing") {
        return t("vue.dashboard_overview_trial_ends", {
            date: formatDate(periodEndsAt),
        });
    }

    if (periodEndsAt !== null && props.subscription.state === "cancelled") {
        return t("vue.dashboard_overview_access_ends", {
            date: formatDate(periodEndsAt),
        });
    }

    if (props.subscription.billingDateRelevant === false) {
        return null;
    }

    if (props.nextBillingAt !== null) {
        return t("vue.dashboard_overview_next_billing", {
            date: formatDate(props.nextBillingAt),
        });
    }

    return props.billingLoading === true
        ? t("vue.dashboard_overview_loading")
        : null;
});

const hiddenMemberCount = computed<number>(() =>
    Math.max(props.team.memberCount - props.team.members.length, 0),
);

const roleLabel = computed<string>(() =>
    props.team.currentUserRole === null
        ? t("vue.dashboard_overview_role_unknown")
        : t(`vue.dashboard_overview_role_${props.team.currentUserRole}`),
);

const teamRows = computed<TeamRow[]>(() => [
    {
        label: t("vue.dashboard_overview_team_members"),
        value: String(props.team.memberCount),
    },
    {
        label: t("vue.dashboard_overview_team_pending_invitations"),
        value: String(props.team.pendingInvitationCount),
    },
    {
        label: t("vue.dashboard_overview_team_your_role"),
        value: roleLabel.value,
    },
]);
</script>
